<template>
  <div class="car-model-table section-card">
    <div class="section-title">选择租赁方案</div>
    <base-list-management ref="baseListRef" :configData="configData" />
  </div>
</template>

<script setup>
import { getBatchCarModels, toggleCarModel } from '@/api/goods'
// import { useStoreGetters } from '@/store/hooks'
import { debounce } from 'lodash'
import { nextTick, ref, watch } from 'vue'
import { carModelTableConfig } from '../config/carModelTable'
import { useBatchSession } from '../hooks/useBatchSession'

const emit = defineEmits(['openPlanDialog', 'openCarInfoDialog'])

const { sessionId, globalCarInfo } = useBatchSession()

// 获取城市列表
// const getters = useStoreGetters(['allCities'])
// const allCities = computed(() => getters.allCities)

const baseListRef = ref(null)

// 检查是否可选（根据服务端返回的 canSelect 字段）
const checkSelectable = (row) => {
  return row.canSelect !== false
}

// 设置车型信息
const handleSetCarInfo = (row) => {
  emit('openCarInfoDialog', row)
}

// 配置已选方案
const handleConfigPlan = (row) => {
  emit('openPlanDialog', row)
}

// 处理单个checkbox点击（防抖处理）
const handleSelect = debounce(async (selection, row) => {
  // 判断当前行是否被选中
  const isSelected = selection.some(
    (item) => item.carModelId === row.carModelId,
  )

  // 调用API同步到服务端
  const res = await toggleCarModel({
    sessionId: sessionId.value,
    carModelId: row.carModelId,
    checked: isSelected,
  })

  // API 成功后，用返回的数据更新整行
  if (res?.code === 1 && res.data) {
    const list = baseListRef.value?.$refs?.baseTable?.list
    if (!list) return

    const targetRow = list.find((item) => item.carModelId === row.carModelId)

    if (targetRow) {
      // 直接用后端返回的数据更新整行
      Object.assign(targetRow, res.data)
    }
  }
}, 300)

// 同步表格选中状态（根据服务端返回的 select 字段）
const syncTableSelection = () => {
  const tableInstance =
    baseListRef.value?.$refs?.baseTable?.$refs?.blmCommonTable?.$refs?.table
  if (!tableInstance) return

  // 根据服务端返回的 select 和 canSelect 字段设置选中状态
  const list = baseListRef.value?.$refs?.baseTable?.list || []
  list.forEach((row) => {
    const shouldSelect = row.select && row.canSelect
    tableInstance.toggleRowSelection(row, shouldSelect)
  })
}

// API 函数 - 直接返回后端数据
const apiFn = async (params) => {
  const { brandSeriesModel, cityCode, pageNum, pageSize } = params

  const res = await getBatchCarModels({
    sessionId: sessionId.value,
    age: globalCarInfo.value?.carAge,
    carSeriesId: brandSeriesModel?.seriesId,
    carModelId: brandSeriesModel?.modelId,
    cityCode,
    pageNum,
    pageSize,
  })

  return res
}

// 自定义重置函数
const handleCustomizeReset = () => {
  // BaseListManagement 内部的自定义表单组件数据需要手动清空
  baseListRef.value?.setItemData({
    elConfigKey: 'value',
    key: 'brandSeriesModel',
    data: {},
  })
}

// 监听表格数据变化，自动同步选中状态
watch(
  () => baseListRef.value?.$refs?.baseTable?.list,
  (newList, oldList) => {
    // 只有当列表数据真正改变时才同步（避免初始化时重复触发）
    if (newList && newList.length > 0 && newList !== oldList) {
      nextTick(() => {
        syncTableSelection()
      })
    }
  },
)

// 生成配置
const configData = ref(
  carModelTableConfig({
    handleSetCarInfo,
    handleConfigPlan,
    handleSelect,
    checkSelectable,
    apiFn,
    handleCustomizeReset,
  }),
)

// 刷新列表
const refresh = () => {
  baseListRef.value?.searchForm()
}

// 是否有选中的车型（从表格数据中判断）
const hasSelectedModels = () => {
  const list = baseListRef.value?.$refs?.baseTable?.list || []
  return list.some((row) => row.select && row.canSelect)
}

// 设置城市选项
// const setCityOptions = (cities) => {
//   const cityItem = configData.value.formData.list.find(
//     (item) => item.itemConfig?.prop === 'cityCode',
//   )
//   if (cityItem?.elConfig?.options) {
//     cityItem.elConfig.options.list = cities
//   }
// }

// 监听城市列表变化（异步加载情况）
// watch(
//   allCities,
//   (cities) => {
//     if (cities?.length) {
//       setCityOptions(cities)
//     }
//   },
//   { immediate: true },
// )

defineExpose({
  refresh,
  hasSelectedModels,
})
</script>

<style lang="scss" scoped>
.car-model-table {
  // 隐藏表头的全选checkbox
  :deep(.el-table__header-wrapper .el-table__header .el-checkbox) {
    display: none;
  }

  :deep(.model-name) {
    .brand {
      color: #909399;
      margin-right: 4px;
    }
    .name {
      color: #303133;
    }
  }

  :deep(.status-custom) {
    color: #e6a23c;
  }
}
</style>
